import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import cx from "classnames";
import { graphql, Link } from "gatsby";
import React, { ReactNode, useState } from "react";
import { Helmet } from "react-helmet";
import type {
  CompareWithPageContext,
  PathPageContext,
  SERPPageContext,
} from "../../gatsby-node";
import { SERPConfig } from "../../plugins/gatsby-source-serp/types";
import useAbPathRedirect from "../hooks/useABPathRedirect";
import useFormValuesFromURL, {
  AllowedSearchParams,
} from "../hooks/useFormValuesFromURL";
import { fetchJobs, getQueryKey } from "../hooks/useJobsQuery";
import usePath1061Redirect from "../hooks/usePath1061Redirect";
import { lang } from "../live-sites";
import BingTracking from "./BingTracking";
import CompanyLogo, { getCompanyLogo } from "./CompanyLogo";
import CookieBanner, { CookieBannerTx } from "./CookieBanner";
import ErrorBoundary from "./ErrorBoundary";
import Favicon from "./Favicon";
import Footer, { type FooterTx } from "./Footer";
import HotJar from "./HotJar";
import Loader from "./Loader";
import LogoSVG from "./LogoSVG";
import Pushnami from "./Pushnami";

const GATSBY_COUNTRY = process.env.GATSBY_COUNTRY;
const GATSBY_DOMAIN = process.env.GATSBY_DOMAIN;
const API_DOMAIN = `https://api-v2.${GATSBY_DOMAIN}`;

function NoScript() {
  return (
    <noscript>
      <div className="flex items-start justify-center rounded-md bg-red-50 p-12">
        <ExclamationCircleIcon
          className="h-7 w-7 text-yellow-400"
          aria-hidden="true"
        />
        <h3 className="text-md ml-3 font-medium text-yellow-800">
          It looks like JavaScript is disabled. Please enable it to use this
          website.
        </h3>
      </div>
    </noscript>
  );
}

export default function Layout({
  children,
  title,
  description,
  footerTx,
  cookieBannerTx,
  className = "relative mx-auto w-full max-w-7xl px-8 pt-4 sm:px-20 mb-20",
  pageContext,
  location,
  site,
  HeaderLink,
  positionOverride,
  noindex,
  enableHotJar = false,
}: {
  children: ReactNode;
  title: string;
  description?: string;
  location: Location;
  cookieBannerTx: CookieBannerTx;
  footerTx: FooterTx;
  className?: string;
  site: Queries.Site;
  HeaderLink?: ReactNode;
  positionOverride?: SERPConfig["positionOverride"];
  pageContext: PathPageContext | CompareWithPageContext | SERPPageContext;
  noindex?: true;
  enableHotJar?: boolean;
}) {
  const { pathname } = location;
  const [companyLogo, setCompanyLogo] = useState<string | null | undefined>(
    null
  );
  const queryClient = useQueryClient();

  useFormValuesFromURL(location, (searchParams: AllowedSearchParams) => {
    const {
      location,
      position,
      email,
      firstname,
      lastname,
      phone,
      utm_campaign,
      utm_content,
      utm_medium,
      utm_source,
      utm_term,
      qrn_campaign,
      qrn_content,
      qrn_medium,
      qrn_source,
      qrn_term,
      gclid,
      jobhash,
      jobtitle,
      company,
    } = searchParams;

    setCompanyLogo(
      getCompanyLogo(pathname, position, pageContext.templateFileName)
    );

    pageContext.prefetchQueries?.forEach(
      (prefetchQuery: PathPageContext["jobQuery"]) => {
        const prefetchQueryParams = {
          location,
          email,
          firstname,
          lastname,
          phone,
          utm_campaign,
          utm_content,
          utm_medium,
          utm_source,
          utm_term,
          qrn_campaign,
          qrn_content,
          qrn_medium,
          qrn_source,
          qrn_term,
          gclid,
          jobhash,
          jobtitle,
          company,
          position: prefetchQuery.position || position,
          sources: prefetchQuery.sources,
          offset: prefetchQuery.offset,
          source: prefetchQuery.source,
          limit: prefetchQuery.limit,
          withRgDirect: prefetchQuery.withRgDirect,
          queryName: prefetchQuery.queryName,
        };
        const queryKey = getQueryKey(prefetchQueryParams);
        queryClient.prefetchQuery(queryKey, () =>
          fetchJobs(prefetchQueryParams, positionOverride)
        );
      }
    );
  });

  const isPath1061Redirecting = usePath1061Redirect(location);
  const isRedirecting = useAbPathRedirect(
    location,
    pageContext as PathPageContext
  );
  if (isRedirecting || isPath1061Redirecting) {
    return (
      <div className="grid h-screen w-full place-content-center">
        <Loader />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Helmet titleTemplate={`%s | ${site.siteMetadata.siteTitle}`}>
        <html lang={lang} />
        <meta charSet="utf-8" />
        <title>{title}</title>
        <meta name="description" content={description} />
        {noindex && <meta name="robots" content="noindex" />}
        <link rel="manifest" href="/manifest.json"></link>
        <link rel="preconnect" href={`https://api-v2.${GATSBY_DOMAIN}`} />
        <link rel="preconnect" href="https://www.google-analytics.com"></link>
        <link rel="preconnect" href={API_DOMAIN}></link>
        <link rel="dns-prefetch" href={API_DOMAIN}></link>
        <link
          rel="preconnect"
          href="https://pagead2.googlesyndication.com"
        ></link>
        <link
          rel="dns-prefetch"
          href="https://pagead2.googlesyndication.com"
        ></link>
        <meta name="google-adsense-account" content="sites-9053884276298326" />
      </Helmet>
      <Favicon />
      {site!.siteMetadata!.pushnamiId ? (
        GATSBY_DOMAIN === "best-jobs-online.com" && GATSBY_COUNTRY === "uk" ? (
          <iframe
            src="https://uk.just-jobs.com/push"
            width="1px"
            height="1px"
            title="Just Jobs Pushnami Widget"
          ></iframe>
        ) : (
          <Pushnami pushnamiId={site.siteMetadata.pushnamiId} />
        )
      ) : null}
      <BingTracking />

      <HotJar location={location} enableHotJar={enableHotJar} />
      <CookieBanner {...cookieBannerTx} />
      <div className="flex min-h-screen flex-col">
        <NoScript />
        <header className="relative z-10 h-[4rem] border-b bg-white px-2 sm:px-4">
          <div
            className={cx("mx-auto flex h-full items-center py-2 sm:py-6", {
              "justify-center": !HeaderLink,
              "max-w-xl px-4 sm:justify-between lg:max-w-3xl": HeaderLink,
            })}
          >
            {companyLogo ? (
              <CompanyLogo position={companyLogo} />
            ) : (
              <Link
                to={`https://${GATSBY_COUNTRY}.${GATSBY_DOMAIN}`}
                className={cx("flex items-center p-2", {
                  invisible: companyLogo === null,
                })}
              >
                <LogoSVG />
              </Link>
            )}

            {HeaderLink}
          </div>
        </header>
        <main className={className}>{children}</main>
        <Footer {...footerTx} location={location} />
        <script
          id="indeed-tracking"
          src="https://prod.statics.indeed.com/tmn-imp/v4/lazyload.min.js"
        />
      </div>
    </ErrorBoundary>
  );
}

export const fragments = graphql`
  fragment SiteMetadata on Site {
    siteMetadata {
      siteTitle
    }
    ...Pushnami
  }
`;
